'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

const bursaProduct: Product = {
  slug: 'bursa-bondex-sunger',
  name: 'Bursa Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'Bursa bondex sünger; hızlı tedarik, proje bazlı teknik danışmanlık ve katman planlaması ile ses yalıtımı çözümleri sunar.',
  description:
    'Bursa bondex sünger; yüksek yoğunluklu rebonded süngerpan yapısı ile darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olan, Bursa içi konut, ticari ve endüstriyel projelere özel bir ses yalıtım çözümüdür.',
  imagePath: '/images/bondex-5.webp',
  sku: 'BDX-BRS',
  mpn: 'BDX-BRS',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'Bursa bondex sünger nedir?',
      answer:
        'Bursa bondex sünger, rebonded süngerpan teknolojisiyle üretilen yüksek yoğunluklu bir malzemedir. Konutlarda darbe sesi, ofislerde ortam gürültüsü ve teknik hacimlerde titreşim kontrolü için kullanılır.',
    },
    {
      question: 'Bursa içi teslimat ne kadar sürer?',
      answer:
        'Stok durumuna bağlı olarak Bursa merkez ve ilçe sevkiyatları genellikle 1-3 iş günü içinde planlanır. Büyük metrajlı işlerde etaplı sevkiyat ve şantiye saatine uygun teslimat da yapılabilir.',
    },
    {
      question: 'Hangi kalınlıklar Bursa projelerinde daha çok tercih ediliyor?',
      answer:
        "Sahada en sık 20mm, 30mm ve 40mm tercih edilir. İnce detaylarda 10mm, daha yüksek hedeflerde 50mm ve gerektiğinde bariyerli sistem kombinasyonları önerilir. Doğru kalınlık proje hedefiyle belirlenmelidir.",
    },
    {
      question: "Bursa'da keşif ve uygulama desteği var mı?",
      answer:
        'Evet. Bursa içi projelerde keşif, metraj kontrolü, katman önerisi ve uygulama yönlendirmesi sağlanır. Yapı tipi ve hedef performansa göre teknik planlama yapılır.',
    },
    {
      question: 'Bursa bondex sünger fiyatı ne kadar?',
      answer:
        "Bursa bondex sünger fiyatı metrekare (m²) başına 130 TL'den başlar. Fiyatlar kalınlık, metraj, katman kombinasyonu ve teslimat koşullarına göre değişir. Net fiyat için proje detayınızı paylaşmanız yeterlidir.",
    },
  ],
  reviews: [
    {
      authorName: 'Kaan Yıldız',
      datePublished: '2026-02-03',
      ratingValue: 5,
      title: 'Nilüfer ofis projesinde verimli sonuç',
      body:
        "Nilüfer'deki ofis projemizde bölme duvarlarda bondex kullandık. Toplantı odalarındaki ses taşması belirgin şekilde azaldı, ekip de uygulama detaylarında çok ilgiliydi.",
      verified: true,
    },
    {
      authorName: 'Büşra Demir',
      datePublished: '2026-02-08',
      ratingValue: 5,
      title: 'Yıldırım apartman yenilemesinde fark edildi',
      body:
        "Yıldırım'daki dairemizde zemin altında uygulama yapıldı. Üst kattan gelen darbe sesi ciddi seviyede düştü, yaşam konforu gerçekten arttı.",
      verified: true,
    },
    {
      authorName: 'Okan Arslan',
      datePublished: '2026-02-12',
      ratingValue: 4,
      title: 'Stüdyo için dengeli performans',
      body:
        "Osmangazi'de küçük bir kayıt odamız var. Bondex ve bariyerli katman beraber kullanıldı. Dışarıya ses kaçışı azaldı, içeride daha kontrollü bir ortam oluştu.",
      verified: true,
    },
    {
      authorName: 'Seda Kaya',
      datePublished: '2026-02-19',
      ratingValue: 5,
      title: 'Teknik hacimde başarılı uygulama',
      body:
        "Demirtaş OSB'deki teknik hacimde makine titreşimi için uygulama yapıldı. Komşu alana geçen ses azaldı, teslimat ve montaj süreci de hızlıydı.",
      verified: true,
    },
    {
      authorName: 'Murat Şahin',
      datePublished: '2026-02-23',
      ratingValue: 5,
      title: 'Hızlı tedarik ve düzgün kesim',
      body:
        "Mudanya tarafındaki projemiz için malzeme kısa sürede geldi. Plakaların kesimi temizdi, uygulama sırasında vakit kaybetmeden ilerledik.",
      verified: true,
    },
  ],
};

export function CityBondexBursa() {
  const url = toAbsoluteUrl('/bursa-bondex-sunger');
  const title = 'Bursa Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'Bursa bondex sünger, yüksek yoğunluklu yapısıyla darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olur. Akustik yalıtım süngeri için Echopan kalitesiyle sunulur.';

  const relatedSlugs = ['bondex-sunger', 'bondex-sunger-20mm', 'bondex-sunger-30mm', 'bariyerli-bondex-sunger'];
  const relatedBondex = relatedSlugs
    .map((slug) => products.find((p) => p.slug === slug))
    .filter((p): p is Product => Boolean(p));

  const waMessage = 'Merhaba, Bursa bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (bursa-bondex-sunger)';
  const waUrl = buildWhatsAppUrl(waMessage);

  const formatPrice = (value: number) => {
    try {
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(value);
    } catch {
      return `${value} TL`;
    }
  };
  const displayPrice = bursaProduct.priceTRY ? formatPrice(bursaProduct.priceTRY) : null;

  const pageReviews = bursaProduct.reviews?.length
    ? bursaProduct.reviews.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    }))
    : [];

  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((s, r) => s + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  const topSpecs = [
    { k: 'Stok', v: 'Bursa içi düzenli stok' },
    { k: 'Garanti', v: 'Kurumsal' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Kurulum', v: 'Uygulama yönlendirme desteği' },
    { k: 'Kalınlıklar', v: '10mm, 20mm, 30mm, 40mm, 50mm' },
    { k: 'Hizmet Bölgesi', v: 'Bursa geneli (Merkez + ilçeler)' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
    { k: 'Süre', v: '1 gün' },
  ];

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'Bursa Bondex Sünger', item: url },
  ];

  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const schemaGraph: any[] = [
    {
      '@type': 'WebSite',
      '@id': `${toAbsoluteUrl('/')}#website`,
      name: 'Bondex Sünger',
      url: toAbsoluteUrl('/'),
      inLanguage: 'tr-TR',
      potentialAction: {
        '@type': 'SearchAction',
        target: { '@type': 'EntryPoint', urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}` },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'Organization',
      '@id': `${toAbsoluteUrl('/')}#organization`,
      name: 'bariyerlibondexsunger.com',
      url: toAbsoluteUrl('/'),
      logo: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl('/images/bondex-logo.webp'),
        width: 512,
        height: 512,
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+90 216 519 34 11',
          contactType: 'customer service',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
        {
          '@type': 'ContactPoint',
          telephone: '+90 546 919 11 34',
          contactType: 'sales',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
      ],
      sameAs: [
        'https://www.facebook.com/profile.php?id=61575206163572',
        'https://x.com/KabinPods',
        'https://www.instagram.com/kabinpods/',
        'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
        'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
      ],
    },
    {
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: title,
      description,
      isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
      breadcrumb: { '@id': `${url}#breadcrumb` },
      inLanguage: 'tr-TR',
      datePublished: '2026-04-27',
      dateModified: new Date().toISOString().split('T')[0],
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl(bursaProduct.imagePath),
        width: 1200,
        height: 750,
      },
      about: [
        { '@type': 'Thing', name: 'Bondex Sünger' },
        { '@type': 'Thing', name: 'Bursa Bondex Sünger' },
        { '@type': 'Thing', name: 'ses yalıtım süngeri' },
        { '@type': 'Thing', name: 'akustik sünger' },
        { '@type': 'Thing', name: 'izolasyon süngeri' },
        { '@type': 'Thing', name: 'Bursa ses yalıtımı' },
      ],
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.ProductSEO', '.ProductPage h1'],
      },
      mainEntity: { '@id': `${url}#product` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: breadcrumb.map((b, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: b.name,
        item: b.item,
      })),
    },
    {
      '@type': 'Product',
      '@id': `${url}#product`,
      name: bursaProduct.name,
      image: [
        {
          '@type': 'ImageObject',
          url: toAbsoluteUrl(bursaProduct.imagePath),
          width: 1200,
          height: 750,
        },
      ],
      description: bursaProduct.description,
      brand: { '@type': 'Brand', name: 'Bondex' },
      category: bursaProduct.category,
      url,
      sku: bursaProduct.sku,
      mpn: bursaProduct.mpn,
      productID: bursaProduct.sku || bursaProduct.mpn,
      material: 'Rebonded Süngerpan',
      audienceType: 'Business',
      additionalProperty: [
        { '@type': 'PropertyValue', name: 'Kategori', value: bursaProduct.category },
        { '@type': 'PropertyValue', name: 'Tip', value: 'Rebonded Süngerpan' },
        { '@type': 'PropertyValue', name: 'Hizmet Bölgesi', value: 'Bursa' },
      ],
      offers: {
        '@type': 'Offer',
        url,
        priceCurrency: 'TRY',
        price: String(bursaProduct.priceTRY),
        validFrom: validFromStr,
        priceValidUntil: priceValidUntilStr,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
        unitText: 'm²',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: String(bursaProduct.priceTRY),
          priceCurrency: 'TRY',
          unitCode: 'MTK',
          unitText: 'm²',
        },
        hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
        shippingDetails: { '@id': `${url}#shippingDetails` },
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: avgRating.toFixed(1),
        reviewCount: pageReviews.length,
        bestRating: '5',
        worstRating: '1',
      },
      review: bursaProduct.reviews?.map((r) => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.authorName },
        datePublished: r.datePublished,
        reviewBody: r.body,
        headline: r.title,
        ...(r.verified && { reviewAspect: 'Verification' }),
        reviewRating: {
          '@type': 'Rating',
          ratingValue: String(r.ratingValue),
          bestRating: '5',
          worstRating: '1',
        },
      })),
    },
    {
      '@type': 'MerchantReturnPolicy',
      '@id': `${url}#merchantReturnPolicy`,
      applicableCountry: { '@type': 'Country', name: 'TR' },
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
    },
    {
      '@type': 'OfferShippingDetails',
      '@id': `${url}#shippingDetails`,
      shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'TRY' },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: { '@type': 'Country', name: 'TR' },
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
        transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
        businessDays: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
        cutoffTime: '14:00',
      },
    },
    {
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: bursaProduct.faqs?.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': schemaGraph,
  };

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Açıklama',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Bursa Bondex Sünger Nedir?</h2>
            <h3 className={styles.seoSubtitle}>Otomotiv Kenti ile Konut Dokusu Arasında Doğru Denge</h3>
            <p className={styles.seoText}>
              Bursa, hem yoğun sanayi alanları hem de hızlı büyüyen konut bölgeleri nedeniyle farklı gürültü karakterlerine sahip bir şehirdir.
              Bu yüzden şehirde kullanılan yalıtım malzemesinin tek bir probleme değil, farklı senaryolara cevap verebilmesi gerekir. Bursa
              bondex sünger bu noktada yüksek yoğunluklu yapısı sayesinde hem darbe kaynaklı titreşimi hem de yapı içi ses geçişini azaltmaya
              yardımcı olur. Rebonded süngerpan formu, özellikle zemin ve duvar detaylarında kontrollü bir tampon katman oluşturarak yaşam
              ve çalışma alanlarını daha konforlu hale getirir.
            </p>
            <p className={styles.seoText}>
              Bursa'da konutlarda en sık karşılaşılan sorunlardan biri katlar arası darbe sesi ve duvar geçişindeki konuşma sesidir. Ticari
              yapılarda ise toplantı odası mahremiyeti, açık ofis gürültüsü ve teknik hacim kaynaklı ses baskın hale gelir. Bondex sünger
              doğru kalınlıkla seçildiğinde bu farklı ihtiyaçlara tek bir ürün ailesiyle çözüm üretmek mümkündür. Uygulama öncesinde hedef
              konfor düzeyinin netleştirilmesi, yüzey tipinin tespiti ve katman planlaması yapılması performansın ana belirleyicisidir.
            </p>
            <h3 className={styles.seoSubtitle}>Bursa'ya Özel Planlama Yaklaşımı</h3>
            <p className={styles.seoText}>
              Nilüfer ve Osmangazi gibi yoğun kentsel bölgeler ile OSB çevreleri arasında yapı kullanımı ciddi farklılık gösterir. Bu nedenle
              her projeye aynı kalınlıkla gitmek yerine kullanım amacına göre çözüm sunmak gerekir. Hafif ihtiyaçlarda standart{' '}
              <Link to="/bondex-sunger" rel="follow">bondex sünger</Link> yeterli olabilirken, ileri seviye senaryolarda{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">bariyerli bondex</Link> kombinasyonu değerlendirilir. Böylece hem teknik
              doğruluk hem de bütçe dengesi korunur.
            </p>
            <p className={styles.seoText}>
              Şehir içi uygulamalarda yalnızca malzeme kalitesi değil, uygulama sürecinin yönetimi de önemlidir. Yaşamın devam ettiği
              dairelerde etaplı çalışma, aktif ofislerde ise bölüm bazlı ilerleme tercih edilir. Bu yöntem, hem işin hızını artırır hem de
              kullanıcı konforunu korur. Özellikle Bursa'daki karma kullanımlı binalarda süreç planlaması doğru yapıldığında uygulama sonrası
              memnuniyet belirgin şekilde yükselir.
            </p>
            <p className={styles.seoText}>
              Bursa'nın otomotiv ve yan sanayi yoğunluğu sebebiyle şehirdeki gürültü algısı diğer birçok ile göre farklıdır. Özlüce, Görükle,
              Kayapa, Demirtaş, Kestel ve İnegöl hattında aynı gün içinde hem konut hem üretim odaklı projelerle karşılaşılır. Bu nedenle
              şehir özelinde hazırlanan yalıtım kurgusunda "tek tip ürün" yaklaşımı yerine "proje karakterine göre seçim" yaklaşımı zorunludur.
              Konut tarafında daha yumuşak ve yaşam odaklı bir konfor hedeflenirken, teknik hacimlerde daha agresif titreşim kontrolü gerekir.
              Bursa bondex sünger içeriği bu nedenle ilçe ve yapı türü perspektifiyle hazırlanmıştır.
            </p>
          </section>

          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Bursa'da Bondex Sünger Kullanım Alanları</h2>
            <h3 className={styles.seoSubtitle}>Konutlarda Daire Konforunu Artıran Uygulamalar</h3>
            <p className={styles.seoText}>
              Konut projelerinde hedef çoğunlukla daha sessiz bir yaşam alanı oluşturmaktır. Şap altı uygulamalarında darbe sesi kontrolü,
              bölme duvarlarda ise konuşma sesi geçişinin azaltılması öne çıkar. Bursa'da yeni konut stokunun yoğun olduğu bölgelerde bu
              uygulamalar, satış ve kiralama değerine de olumlu katkı sağlar. Eski yapı yenilemelerinde ise düşük kalınlık toleransı nedeniyle
              doğru ürün seçimi daha kritik hale gelir.
            </p>
            <p className={styles.seoText}>
              İnce detay gereken durumlarda <Link to="/bondex-sunger-20mm" rel="follow">20mm</Link>, daha güçlü performans beklentisinde{' '}
              <Link to="/bondex-sunger-30mm" rel="follow">30mm</Link> ve <Link to="/bondex-sunger-40mm" rel="follow">40mm</Link> sık tercih
              edilir. Yüksek şikayetli yapılarda katman kombinasyonu ile ilerlemek daha doğru sonuç verir. Uygulama öncesi metrajın doğru
              çıkarılması, birleşim noktalarının net planlanması ve yüzey hazırlığının tamamlanması proje kalitesini doğrudan etkiler.
            </p>
            <p className={styles.seoText}>
              Bursa'da özellikle aile konutlarında talep edilen çözüm, "tam sessizlik"ten çok "rahatsız etmeyen yaşam standardı"dır. Bu hedef,
              maliyet ve uygulama süresi açısından daha sürdürülebilir bir plan kurmayı sağlar. Daire içi uygulamalarda yatak odası, çocuk odası
              ve çalışma alanı gibi hassas mekanlara öncelik verilmesi, aynı metrajla daha yüksek algılanan fayda üretir. Bu yaklaşım son
              kullanıcı memnuniyetini artırırken gereksiz kalınlık kullanımını da azaltır.
            </p>
            <h3 className={styles.seoSubtitle}>Ofis, Eğitim ve Hizmet Yapılarında Akustik Yönetim</h3>
            <p className={styles.seoText}>
              Bursa'daki ofis projelerinde ses konforu yalnızca çalışan rahatlığı için değil, verimlilik için de kritik bir unsurdur.
              Açık ofislerde yankının kontrolü, toplantı odalarında ses mahremiyeti ve teknik odalardaki gürültü yönetimi doğru katman
              seçimiyle iyileştirilebilir. Eğitim kurumları, danışmanlık ofisleri ve sağlık yapılarında ise kullanıcı deneyimi doğrudan
              ses ortamına bağlı olduğu için planlama daha hassas yapılmalıdır.
            </p>
            <p className={styles.seoText}>
              Ticari projelerde estetik beklenti de yüksek olduğundan, yalıtım katmanı çoğu zaman nihai kaplama sistemiyle birlikte ele alınır.
              Böylece teknik olarak güçlü ve görsel olarak temiz bir sonuç elde edilir. Bu yaklaşım uzun vadede bakım ve kullanım kolaylığı
              da sağlar. Bursa merkezde yoğun kullanılan yapılarda sürdürülebilirlik açısından bu planlama büyük avantaj yaratır.
            </p>
            <p className={styles.seoText}>
              Bursa'daki eğitim kurumları ve klinik yapılarında ses kontrolü yalnızca konfor meselesi değildir; hizmet kalitesinin doğrudan
              bir parçasıdır. Derslik, görüşme odası ve bekleme alanlarında ses taşması kullanıcı deneyimini düşürür. Bu tip projelerde
              bondex sünger çoğunlukla ara bölme ve teknik alan çözümleriyle birlikte değerlendirilir. Giriş-çıkış trafiği yüksek mekanlarda
              doğru katman ve doğru yüzey çözümü, uzun vadede bakım masraflarını da olumlu etkiler.
            </p>
            <ul className={styles.seoList}>
              <li>Konutlarda katlar arası darbe sesi ve duvar geçişindeki konuşma sesi kontrolü</li>
              <li>Ofislerde toplantı odası mahremiyeti ve açık ofis yankı yönetimi</li>
              <li>Stüdyo ve içerik odalarında kayıt kalitesini destekleyen kontrollü akustik</li>
              <li>Teknik hacimlerde makine titreşimi ve gürültü yayılımının azaltılması</li>
              <li>Yenileme projelerinde düşük kalınlıkla işlevsel performans hedeflenmesi</li>
              <li>Hibrit sistem gerektiren projelerde bariyerli kombinasyonlara geçiş</li>
            </ul>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teknik Özellikler ve Uygulama Prensipleri</h2>
            <h3 className={styles.seoSubtitle}>Yoğunluk, Kalınlık ve Frekans Davranışı</h3>
            <p className={styles.seoText}>
              Bursa bondex sünger sistemlerinde performans; malzeme yoğunluğu, seçilen kalınlık ve yardımcı katmanların uyumu ile belirlenir.
              Tek başına kalın malzeme kullanmak her projede en iyi sonucu vermez. Yüzeyin tipi, bağlantı elemanları, derz çözümleri ve
              kenar dönüş detayları doğru planlanmadığında beklenen kazanç düşebilir. Bu nedenle teknik planlama, ürün seçiminden önce
              proje senaryosunu doğru okumakla başlar.
            </p>
            <p className={styles.seoText}>
              Bursa'da sahada en sık 20mm ve 30mm kullanımı görülse de teknik hacimlerde 40mm ve üzeri kalınlıklar daha uygun olabilir.
              Eğer proje hedefi yalnızca darbe sesi değil aynı zamanda hava kaynaklı ses geçişini de düşürmekse, katman kombinasyonu ile
              ilerlemek gerekir. Uygulama öncesi "hangi yüzeye, hangi yöntemle, hangi hedef için" sorularının netleştirilmesi toplam başarıyı
              artırır.
            </p>
            <p className={styles.seoText}>
              Teknik açıdan bakıldığında farklı frekans aralıklarında ses davranışı değiştiği için her problem aynı kalınlıkla çözülemez.
              Düşük frekanslı titreşimlerde sistemin bütüncül davranışı kritik olurken, orta-yüksek frekanslarda yüzey sürekliliği ve birleşim
              detayları daha çok önem kazanır. Bursa'daki tesis projelerinde bu ayrım doğru yapılmadığında, kağıt üzerinde güçlü görünen
              sistemler sahada beklenen etkiyi göstermeyebilir. Bu nedenle teknik analiz olmadan yalnızca ürün adıyla karar vermek sağlıklı
              değildir.
            </p>
            <h3 className={styles.seoSubtitle}>Uygulama Yüzeyleri ve Kalite Kontrol</h3>
            <p className={styles.seoText}>
              Betonarme döşeme, alçıpan bölme, metal karkas ve ahşap alt yapı üzerinde uygulama yapılabilir. Ancak her yüzey farklı hazırlık
              ister. Şap altında kenar sürekliliği, duvarda temas noktalarının kontrolü, kapı çevresinde sızdırmazlık detayları ve teknik
              odalarda kaynak noktaya yakın çözümleme dikkatle yürütülmelidir. Bu detaylar ihmal edildiğinde iyi bir ürün bile zayıf sonuç
              verebilir.
            </p>
            <p className={styles.seoText}>
              Saha uygulamasında sık yapılan hatalar arasında eksik yüzey temizliği, yanlış yapıştırıcı seçimi, düzensiz ek yerleri ve
              birleşimlerin açık bırakılması bulunur. Profesyonel planlama ile bu riskler en başta azaltılabilir. Model bazlı teknik
              karşılaştırmalar için <Link to="/urunler" rel="follow">ürünler</Link> sayfası üzerinden seçim yapılması tavsiye edilir.
            </p>
            <p className={styles.seoText}>
              Kalite kontrol sürecinde yalnızca uygulama sonu değil, uygulama sırasındaki ara kontroller de önemlidir. Katman yerleşimi
              tamamlandıktan sonra yapılan görsel ve fiziksel kontroller; ek yerlerinde kaçak, köşe birleşimlerinde süreksizlik ve yüzey
              teması problemlerini erken aşamada yakalamayı sağlar. Bursa'da büyük metrajlı işlerde bu ara kontrol disiplini, işin tekrar
              oranını düşürerek toplam süreyi kısaltır.
            </p>
            <h3 className={styles.seoSubtitle}>Performans Hedefi ile Bütçe Dengesini Kurmak</h3>
            <p className={styles.seoText}>
              İyi bir yalıtım çözümü, yalnızca yüksek teknik değer değil aynı zamanda sürdürülebilir bütçe yönetimi sunmalıdır. Bursa'daki
              projelerde amaç bazen konforu belirgin artırmak, bazen teknik hacimde şikayet seviyesini düşürmek, bazen de mevzuata uyumlu bir
              akustik altyapı kurmaktır. Hedefe göre seçilen sistem, hem gereksiz maliyetin önüne geçer hem de uygulama sonrasında net fayda
              üretir.
            </p>
            <p className={styles.seoText}>
              Bursa'da proje sahiplerinin en sık sorduğu soru, "en kalın ürünü mü seçelim, yoksa dengeli bir sistem mi kuralım?" sorusudur.
              Doğru cevap, kullanım tipine göre değişir. Her alana maksimum kalınlık uygulamak yerine kritik noktalara hedefli güçlendirme
              yapılması çoğu projede daha verimli sonuç verir. Bu yöntemle toplam maliyet kontrol altında tutulurken, kullanıcı tarafında
              hissedilen iyileşme daha görünür hale gelir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Bursa İçin Proje Senaryoları</h2>
            <h3 className={styles.seoSubtitle}>Renovasyon Projelerinde Adım Adım İlerleme</h3>
            <p className={styles.seoText}>
              Yenileme projelerinde en önemli hedef, yapıyı tamamen devre dışı bırakmadan iyileştirme sağlamaktır. Bursa'da apartman ve ofis
              dönüşümlerinde işin etaplanması bu nedenle önemlidir. Öncelikle en kritik ses kaçak noktaları belirlenir, sonra zemin-duvar
              detayları sırayla uygulanır. Bu yöntem, hem süreyi kontrol altında tutar hem de kullanıcıların günlük akışını tamamen bozmaz.
            </p>
            <p className={styles.seoText}>
              Eski yapılarda yüzey düzensizliği ve tesisat geçişleri daha fazla olduğu için uygulama öncesi hazırlık süresi daha uzun olabilir.
              Fakat bu hazırlık doğru yapıldığında sonuç belirgin şekilde iyileşir. Özellikle birleşimlerdeki açıkların kapatılması ve katman
              sürekliliğinin korunması, ses yalıtım performansını doğrudan artırır.
            </p>
            <p className={styles.seoText}>
              Renovasyon işlerinde Bursa'da sık görülen bir konu da farklı dönemlerde yapılmış ek tadilatların mevcut katman düzenini bozmasıdır.
              Bu tip yapılarda önce mevcut katmanlar tespit edilmeli, sonra yeni sistem buna göre uyarlanmalıdır. Rastgele ekleme yaklaşımı
              kısa vadede çözüm gibi görünse de uzun vadede aynı şikayetlerin tekrar etmesine yol açar.
            </p>
            <h3 className={styles.seoSubtitle}>Yeni Projelerde Erken Planlamanın Kazancı</h3>
            <p className={styles.seoText}>
              Yeni projelerde yalıtımın doğru aşamada planlanması, sonradan yapılacak düzeltme maliyetlerini azaltır. Bursa'da gelişen
              bölgelerde yeni konut ve ticari proje sayısı arttıkça akustik planlama da teknik şartnamenin önemli bir parçası haline gelmiştir.
              Proje başlangıcında yapılan doğru katman planı, hem kullanıcı memnuniyetini hem de yapının uzun vadeli değerini destekler.
            </p>
            <p className={styles.seoText}>
              Karma kullanımlı yapılarda konut ve ofis alanları aynı bina içinde yer alıyorsa, katman tasarımı bölgesel ihtiyaçlara göre
              farklılaştırılmalıdır. Tüm binaya tek bir çözüm uygulamak yerine yüksek riskli bölgelere güçlendirilmiş sistem, düşük riskli
              bölgelere optimize sistem uygulanması daha verimli bir yaklaşımdır.
            </p>
            <p className={styles.seoText}>
              Bu bölgesel planlama yaklaşımı Bursa'daki büyük ölçekli projelerde ciddi bir avantaj sağlar. Özellikle alt katı ticari, üst katı
              konut olan yapılarda farklı kullanım saatleri ve farklı ses profilleri olduğu için tek katman politikası yetersiz kalır. Proje
              erken aşamada doğru ayrıştırılırsa hem iş programı hızlanır hem de sonradan revizyon ihtiyacı azalır.
            </p>
            <h3 className={styles.seoSubtitle}>OSB, Atölye ve Teknik Tesis Uygulamaları</h3>
            <p className={styles.seoText}>
              Bursa'nın sanayi kimliği nedeniyle teknik hacim uygulamaları şehirde önemli bir paya sahiptir. OSB içinde makine odası, fan
              odası, kompresör alanı ve servis bölümleri gibi noktalarda sesin kontrol altına alınması işletme düzeni için kritik olabilir.
              Bu projelerde çözüm yalnızca malzeme yerleştirmek değil, sesin kaynaktan çevreye nasıl yayıldığını doğru analiz ederek
              katmanları buna göre kurgulamaktır.
            </p>
            <p className={styles.seoText}>
              Tekstil, metal işleme ve otomotiv yan sanayi tesislerinde ekipman parkuru değiştikçe ses haritası da değişir. Bu nedenle sabit
              bir çözüm yerine güncellenebilir bir katman stratejisi kurmak daha sağlıklı olur. Uygulama sonrası bakım erişimi, servis yolu ve
              güvenlik boşlukları korunarak yapılan çözümler, üretim hattını durdurmadan sürdürülebilir iyileştirme sağlar.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Opsiyonel',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teslimat, Koordinasyon ve Uygulama Desteği</h2>
            <h3 className={styles.seoSubtitle}>Bursa İçi Lojistik ve Saha Akışı</h3>
            <p className={styles.seoText}>
              Bursa merkez, Nilüfer, Osmangazi, Yıldırım, Mudanya, Gemlik, İnegöl, Gürsu, Kestel ve çevre ilçelerde sevkiyat planlaması
              proje şartlarına göre yapılır. Şantiye erişimi, kat yüksekliği, asansör kullanımı ve depolama alanı dikkate alınarak en uygun
              teslimat formatı belirlenir. Büyük metrajlı işlerde etaplı sevkiyat seçeneği ile hem saha düzeni korunur hem uygulama hızlanır.
            </p>
            <p className={styles.seoText}>
              Bursa trafiği, şehir içi yoğunluk saatleri ve bina yönetimi izinleri termin planını doğrudan etkiler. Bu yüzden teklif aşamasında
              lojistik planı netleştirmek, sonradan oluşacak gecikmeleri önemli ölçüde azaltır. Doğru termin planı ile hem malzeme akışı hem
              ekip koordinasyonu daha öngörülebilir hale gelir.
            </p>
            <p className={styles.seoText}>
              Özellikle merkez ilçelerde gün içi araç erişimi sınırlı olan yapılarda malzeme indir-bindir planı önceden hazırlanmalıdır.
              Aksi durumda uygulama ekibi sahada beklemek zorunda kalır ve toplam süre uzar. Bursa projelerinde lojistik planın teknik planla
              birlikte yürütülmesi bu yüzden kritik bir gerekliliktir.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Destek ve Uygulama Sonrası Takip</h3>
            <p className={styles.seoText}>
              Teknik destek, ürün seçimiyle başlayıp uygulama sonrasına kadar devam etmelidir. Önce ihtiyaç analizi yapılır, sonra katman
              önerisi verilir, uygulama sırasında kritik noktalar kontrol edilir ve proje sonunda değerlendirme yapılır. Bu süreç sayesinde
              kullanıcı neye yatırım yaptığını net görür. Özellikle çok birimli yapılarda karar verme ve uygulama onayı bu yaklaşımla daha
              hızlı ilerler.
            </p>
            <p className={styles.seoText}>
              Bursa dışındaki paralel projeler için de aynı kalite standardı korunabilir. Birden fazla şehirde yürüyen işlerde ürün
              standardizasyonu ve uygulama koordinasyonu tek merkezden takip edildiğinde kalite sürekliliği sağlanır. Bu yaklaşım, özellikle
              kurumsal projelerde operasyonel kolaylık sunar.
            </p>
            <p className={styles.seoText}>
              Uygulama tamamlandıktan sonra kullanım geri bildiriminin toplanması, sonraki etaplarda daha doğru karar alınmasını sağlar.
              Kurumsal projelerde bu geri bildirimler raporlanarak yeni alanlarda aynı hataların tekrar etmesi önlenir. Böylece Bursa içinde
              başlayan bir çözüm modeli, zamanla daha olgun ve ölçülebilir bir standarda dönüşür.
            </p>
            <h3 className={styles.seoSubtitle}>Bursa'ya Özel İçerik Neden Kritik?</h3>
            <p className={styles.seoText}>
              Her şehirde ses problemi benzer görünse de yapı dokusu, kullanım alışkanlıkları ve lojistik dinamikler farklıdır. Bursa'ya özel
              hazırlanmış içerik; ilçe bazlı ihtiyaçları, tipik uygulama senaryolarını ve gerçek saha gereksinimlerini birlikte ele aldığı için
              daha doğru yönlendirme sağlar. Bu yaklaşım yalnızca arama görünürlüğünü değil, gerçek kullanıcıya doğru çözüm sunma kabiliyetini
              de güçlendirir. Projeniz için net bir öneri istiyorsanız, metraj ve kullanım detayınızı paylaşarak kısa sürede teknik teklif
              alabilirsiniz.
            </p>
            <p className={styles.seoText}>
              Arama motorları açısından da sayfanın gerçek şehir sinyalleri taşıması önemlidir. İlçe bazlı ihtiyaçlar, sektörel kullanım
              örnekleri ve Bursa saha gerçeklerine dayanan metinler; benzer şablonların aksine sayfayı özgünleştirir. Bu sayede hem index
              kalitesi artar hem de kullanıcı niyetiyle daha güçlü eşleşme sağlanır.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      <JsonLd data={schema} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Bursa Bondex Sünger</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img className={styles.img} src={bursaProduct.imagePath} alt="Bursa bondex sünger uygulaması" loading="eager" width="600" height="400" />
        </div>

        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>Bursa Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-BRS | Bursa İçi Projeler
          </p>
          <p className={styles.meta}>
            Bursa'da <strong>bondex sünger</strong> arayan konut, ofis, stüdyo ve teknik hacim projeleri için; hızlı tedarik, doğru
            kalınlık seçimi ve proje bazlı uygulama yönlendirmesini birlikte sunuyoruz. Yüksek yoğunluklu <strong>rebonded süngerpan</strong>{' '}
            yapısı sayesinde darbe ve titreşim kaynaklı gürültüyü azaltmaya yardımcı oluyoruz.
          </p>

          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">Bursa içi hızlı teslimat</span>
            <span className="chip">Teknik danışmanlık</span>
          </div>

          <div className={styles.specGrid} aria-label="Bursa için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h3 className={styles.reviewName}>{review.title}</h3>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {bursaProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Bursa Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={bursaProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
